import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouterState } from "@tanstack/react-router";

export const ALLE_PROJEKTE = "alle" as const;
export type ProjektAuswahl = string | typeof ALLE_PROJEKTE;

const SPEICHERSCHLUESSEL = "cubic-studio:projekt-auswahl";

type ProjektAuswahlKontext = {
  auswahl: ProjektAuswahl;
  setzeAuswahl: (auswahl: ProjektAuswahl) => void;
};

const Kontext = createContext<ProjektAuswahlKontext | null>(null);

export function projektIdAusPfad(pfad: string): string | null {
  const treffer = /^\/projekte\/([^/?#]+)/.exec(pfad);
  return treffer?.[1] ? decodeURIComponent(treffer[1]) : null;
}

export function initialeProjektAuswahl(
  gespeichert: string | null,
  pfad: string,
): ProjektAuswahl {
  if (gespeichert === ALLE_PROJEKTE || gespeichert?.trim()) {
    return gespeichert as ProjektAuswahl;
  }
  return projektIdAusPfad(pfad) ?? ALLE_PROJEKTE;
}

function liesGespeicherteAuswahl(): string | null {
  try {
    return globalThis.localStorage?.getItem(SPEICHERSCHLUESSEL) ?? null;
  } catch {
    return null;
  }
}

function speichereAuswahl(auswahl: ProjektAuswahl) {
  try {
    globalThis.localStorage?.setItem(SPEICHERSCHLUESSEL, auswahl);
  } catch {
    // Die Auswahl funktioniert auch ohne Webspeicher für die laufende Sitzung.
  }
}

export function ProjektAuswahlProvider({ children }: { children: ReactNode }) {
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  const [auswahl, setzeIntern] = useState<ProjektAuswahl>(() =>
    initialeProjektAuswahl(liesGespeicherteAuswahl(), pfad),
  );

  const wert = useMemo<ProjektAuswahlKontext>(
    () => ({
      auswahl,
      setzeAuswahl: (naechsteAuswahl) => {
        speichereAuswahl(naechsteAuswahl);
        setzeIntern(naechsteAuswahl);
      },
    }),
    [auswahl],
  );

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useProjektAuswahl() {
  const kontext = useContext(Kontext);
  if (!kontext) {
    throw new Error("useProjektAuswahl benötigt den ProjektAuswahlProvider.");
  }
  return kontext;
}

export function einzelnesProjekt(auswahl: ProjektAuswahl): string | undefined {
  return auswahl === ALLE_PROJEKTE ? undefined : auswahl;
}
